import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useToast } from '../../hooks/use-toast';
import { Search, Eye, UserX, UserCheck, Shield, LogIn, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from "../../lib/backendUrl";
const API = `${BACKEND_URL}/api`;

const AdminUsers = () => {
  const { token, login } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchUsers = async (p = page, s = search) => {
    try {
      const res = await axios.get(`${API}/admin/users`, { headers, params: { page: p, limit: 20, search: s } });
      setUsers(res.data.users);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch {}
  };

  useEffect(() => { fetchUsers(); }, [page]);

  const handleSearch = () => { setPage(1); fetchUsers(1, search); };

  const viewUser = async (userId) => {
    try {
      const res = await axios.get(`${API}/admin/users/${userId}`, { headers });
      setSelected(res.data);
    } catch { toast({ title: 'Error', description: 'Failed to load user', variant: 'destructive' }); }
  };

  const toggleActive = async (userId, currentActive) => {
    await axios.put(`${API}/admin/users/${userId}`, { is_active: !currentActive }, { headers });
    toast({ title: currentActive ? 'User suspended' : 'User activated' });
    fetchUsers();
    if (selected?.id === userId) viewUser(userId);
  };

  const toggleAdmin = async (userId, currentAdmin) => {
    await axios.put(`${API}/admin/users/${userId}`, { is_admin: !currentAdmin }, { headers });
    toast({ title: currentAdmin ? 'Admin removed' : 'Admin granted' });
    fetchUsers();
    if (selected?.id === userId) viewUser(userId);
  };

  const impersonate = async (userId) => {
    try {
      const res = await axios.post(`${API}/admin/users/${userId}/impersonate`, {}, { headers });
      localStorage.setItem('impersonating', 'true');
      localStorage.setItem('admin_token', token);
      localStorage.setItem('token', res.data.access_token);
      window.location.href = '/dashboard';
    } catch (err) {
      toast({ title: 'Impersonation failed', description: err.response?.data?.detail, variant: 'destructive' });
    }
  };

  return (
    <div data-testid="admin-users-page">
      <h1 className="text-2xl font-bold text-white mb-1">Users</h1>
      <p className="text-gray-500 text-sm mb-6">Manage all registered users ({total} total)</p>

      <div className="flex gap-2 mb-4">
        <Input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSearch()} placeholder="Search by email, name, or business..." className="bg-[#132218] border-emerald-900/30 text-white" data-testid="admin-user-search" />
        <Button onClick={handleSearch} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="admin-user-search-btn"><Search className="w-4 h-4" /></Button>
      </div>

      <div className="flex gap-6">
        {/* User list */}
        <div className="flex-1">
          <Card className="bg-[#132218] border-emerald-900/30">
            <CardContent className="p-0">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-emerald-900/30">
                    <th className="text-left text-xs text-gray-500 font-medium p-3">User</th>
                    <th className="text-left text-xs text-gray-500 font-medium p-3">Status</th>
                    <th className="text-left text-xs text-gray-500 font-medium p-3">Joined</th>
                    <th className="text-right text-xs text-gray-500 font-medium p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id} className={`border-b border-emerald-900/10 hover:bg-white/[0.02] cursor-pointer ${selected?.id === u.id ? 'bg-emerald-600/5' : ''}`} onClick={() => viewUser(u.id)} data-testid={`user-row-${u.id}`}>
                      <td className="p-3">
                        <p className="text-white text-sm font-medium">{u.full_name || u.email}</p>
                        <p className="text-gray-500 text-xs">{u.email}</p>
                      </td>
                      <td className="p-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${u.is_active ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
                          {u.is_active ? 'Active' : 'Suspended'}
                        </span>
                        {u.is_admin && <span className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/10 text-amber-400"><Shield className="w-3 h-3" />Admin</span>}
                      </td>
                      <td className="p-3 text-gray-500 text-xs">{new Date(u.created_at).toLocaleDateString()}</td>
                      <td className="p-3 text-right">
                        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); viewUser(u.id); }} className="text-gray-400 hover:text-white"><Eye className="w-4 h-4" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {pages > 1 && (
                <div className="flex items-center justify-between p-3 border-t border-emerald-900/30">
                  <span className="text-xs text-gray-500">Page {page} of {pages}</span>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="text-gray-400"><ChevronLeft className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="text-gray-400"><ChevronRight className="w-4 h-4" /></Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* User detail panel */}
        {selected && (
          <div className="w-80 flex-shrink-0" data-testid="user-detail-panel">
            <Card className="bg-[#132218] border-emerald-900/30 sticky top-6">
              <CardHeader>
                <CardTitle className="text-white text-base">User Detail</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-xs text-gray-500">Full Name</p>
                  <p className="text-white text-sm">{selected.full_name || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Email</p>
                  <p className="text-white text-sm">{selected.email}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Registered</p>
                  <p className="text-white text-sm">{new Date(selected.created_at).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Business</p>
                  <p className="text-white text-sm">{selected.business_name || '—'}</p>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="p-2 rounded-lg bg-emerald-600/10"><p className="text-emerald-400 font-bold">{selected.call_count}</p><p className="text-[10px] text-gray-500">Calls</p></div>
                  <div className="p-2 rounded-lg bg-emerald-600/10"><p className="text-emerald-400 font-bold">{selected.invoice_count}</p><p className="text-[10px] text-gray-500">Invoices</p></div>
                  <div className="p-2 rounded-lg bg-emerald-600/10"><p className="text-emerald-400 font-bold">{selected.contact_count}</p><p className="text-[10px] text-gray-500">Contacts</p></div>
                </div>
                {selected.subscription && (
                  <div>
                    <p className="text-xs text-gray-500">Subscription</p>
                    <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-400 capitalize">{selected.subscription.plan_type} — {selected.subscription.status}</span>
                  </div>
                )}
                <div className="space-y-2 pt-2 border-t border-emerald-900/30">
                  <Button onClick={() => toggleActive(selected.id, selected.is_active)} size="sm" className={`w-full text-xs ${selected.is_active ? 'bg-red-600/20 text-red-400 hover:bg-red-600/30' : 'bg-emerald-600/20 text-emerald-400 hover:bg-emerald-600/30'}`} data-testid="toggle-active-btn">
                    {selected.is_active ? <><UserX className="w-3 h-3 mr-1" />Suspend User</> : <><UserCheck className="w-3 h-3 mr-1" />Activate User</>}
                  </Button>
                  <Button onClick={() => toggleAdmin(selected.id, selected.is_admin)} size="sm" className="w-full text-xs bg-amber-600/20 text-amber-400 hover:bg-amber-600/30" data-testid="toggle-admin-btn">
                    <Shield className="w-3 h-3 mr-1" />{selected.is_admin ? 'Remove Admin' : 'Grant Admin'}
                  </Button>
                  <Button onClick={() => impersonate(selected.id)} size="sm" className="w-full text-xs bg-blue-600/20 text-blue-400 hover:bg-blue-600/30" data-testid="impersonate-btn">
                    <LogIn className="w-3 h-3 mr-1" />Impersonate User
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsers;
